import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserAccount } from './user-account';
import { DataSharingService } from './shared/data-sharing.service';
import { ConnectedPeopleInterface } from './user-account';
import { switchMap } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FriendService {
  private searchUsersUrl = `${environment.baseUrl}/searchUsers`;  
  private addFriendUrl = `${environment.baseUrl}/addFriend`;      
  private connectedPeopleUrl = `${environment.baseUrl}/getConnectedPeople`; 
  private removeConnectionUrl = `${environment.baseUrl}/removeConnection`; 
  private acceptFriendUrl = `${environment.baseUrl}/acceptFriend`;
  private refuseFriendUrl = `${environment.baseUrl}/refuseFriend`;
  constructor(private http: HttpClient, private dataSharingService: DataSharingService) { }

  // Method to search users
  searchUsers(query: string): Observable<UserAccount[]> {
    return this.dataSharingService.getActiveUserAccount().pipe(
      switchMap((currentAccount: UserAccount) => {
        const payload = {
          query: query,
          accountId: currentAccount.id // Include the current account ID in the payload
        };
        return this.http.post<UserAccount[]>(this.searchUsersUrl, payload);
      })
    );
  }

  // Method to send a friend request
  addFriend(receiverId: string): Observable<any> {
    return this.dataSharingService.getActiveUserAccount().pipe(
      switchMap((currentAccount: UserAccount) => {
        const payload = {
          sender_id: currentAccount.id, // The ID of the current user sending the request
          receiver_id: receiverId       // The ID of the user to whom the friend request is sent
        };
        return this.http.post<any>(this.addFriendUrl, payload);
      })
    );
  }

  // Method to get connected people using POST
  getConnectedPeople(status: number): Observable<ConnectedPeopleInterface[]> {
    return this.dataSharingService.getActiveUserAccount().pipe(
      switchMap(currentAccount => {
        if (!currentAccount.id) {
          throw new Error('User account is not properly initialized.');
        }

        // Construct the payload with the user's account ID and the status filter
        const payload = {
          account_id: currentAccount.id,
          status: status // Pass the status parameter here to filter results
        };

        // Make the HTTP POST request to the backend API with the payload
        return this.http.post<ConnectedPeopleInterface[]>(this.connectedPeopleUrl, payload);
      })
    );
  }


  // Method to accept a friend request
  acceptFriend(senderId: string): Observable<any> {
    return this.dataSharingService.getActiveUserAccount().pipe(
      switchMap((currentAccount: UserAccount) => {
        const payload = {
          sender_id: senderId,        // The ID of the user who sent the friend request
          receiver_id: currentAccount.id, // The ID of the current user accepting the request
          status: 2                   // Assuming '2' indicates that the request is accepted and they are now friends
        };
        return this.http.post<any>(this.acceptFriendUrl, payload);
      })
    );
  }
  // Method to remove a connection, sending the current user ID and the user to be removed as POST payload
  removeConnection(userId: string): Observable<any> {
    return this.dataSharingService.getActiveUserAccount().pipe(
      switchMap((currentAccount: UserAccount) => {
        if (!currentAccount.id) {
          throw new Error('User account is not properly initialized.');
        }

        // Create the payload containing the current user ID and the user to remove
        const payload = {
          currentUserId: currentAccount.id,
          removeUserId: userId,
          status: 1    
        };

        // Make a POST request with the payload
        return this.http.post<any>(this.removeConnectionUrl, payload);
      })
    );
  }

  // Method to refuse a friend request
  refuseFriend(senderId: string): Observable<any> {
    return this.dataSharingService.getActiveUserAccount().pipe(
      switchMap((currentAccount: UserAccount) => {
        const payload = {
          sender_id: senderId,        // The ID of the user who sent the friend request
          receiver_id: currentAccount.id, // The ID of the current user refusing the request
          status: 3                   // '3' indicates that the request is refused
        };
        return this.http.post<any>(this.refuseFriendUrl, payload);
      })
    );
  }
}
